/* eslint-disable camelcase */
import _get from 'lodash/get';
import _isFinite from 'lodash/isFinite';
import _isNil from 'lodash/isNil';
import {
  getSafariSafeDate,
  getStudentAssignmentStatus,
  shouldHideGradeUntilDueDate,
  getToolIds,
} from '@macmillan-learning/gradebook-shared';
import { isStudentAssignedToAssignment } from '../utils/util';
import { GRADING_TYPE } from '../utils/constants';
import { isWritingFinalPaper } from '../utils/activity';

const shouldShowGrades = (
  isStudentUser,
  percentComplete,
  showGradeToStudent,
  pastDue,
) => {
  // if not a student or doesn't have a extended_data then should show it
  if (_isNil(percentComplete) || !isStudentUser) return true;

  // if not showGradeToStudents and pastDue is false just don't consider other conditions
  if (showGradeToStudent === false && !pastDue) return false;

  // if showGradeToStudents is true and pastDue is true don't check the values
  if (pastDue) return true;

  // if percentComplete == 0 return false
  // if percentComplete > 0 and < 1 return true
  // if percentComplete === 1 return true
  return percentComplete !== 0;
};

export const getStudentGradeObj = (
  assignment,
  studentGrades,
  studentAssignmentExceptions,
  studentDueDateException,
  categories,
  launchLogs,
  student,
  exceptions,
  isBrokenSyncReefGrades,
  isStudentUser,
  gradesToReport, // value got from the settings
) => {
  const {
    id,
    tool,
    name,
    course_id,
    activity_id,
    assignment_due_at,
    gradebook_category_id: gradebookCategoryId,
    assigned_points: assignmentAssignedPoints,
    grading_type,
    subtype,
    sequence,
  } = assignment;

  const { studentId, name: studentName } = student;

  const grade = studentGrades.find(
    (grd) => grd.assignment_id === assignment.id,
  );
  const originalGrade = _get(grade, 'grade', null);
  const isAssigned = isStudentAssignedToAssignment(
    student,
    assignment,
    exceptions,
  );
  const studentAssignmentException = _get(studentAssignmentExceptions, '[0]');

  // Used to determine if a student's grade has been synced with iClicker.
  // This is used to show a status indicator in the instructor
  // gradebook next to the student's name and email
  const isBrokenSyncReefGrade =
    tool === getToolIds().reef && !isBrokenSyncReefGrades;

  // TODO: replace this with applyExceptionsAndDueDate once my module is done
  let dueDate = assignment_due_at && getSafariSafeDate(assignment_due_at);

  if (studentAssignmentException) {
    // If there's a student assignment exception, then apply it
    const exceptionDueDate = _get(
      studentAssignmentException,
      'exception_value.due_date',
    );
    dueDate = getSafariSafeDate(exceptionDueDate);
  } else if (studentDueDateException) {
    // If there's a student due date exception, then apply it
    const exceptionDueDate = _get(
      studentDueDateException,
      'exception_value.due_date',
    );
    dueDate = getSafariSafeDate(exceptionDueDate);
  }

  // Has the assignment due date already passed
  const now = new Date();
  const pastDue = now > dueDate;
  const percentComplete = _get(grade, 'percentComplete', null);
  const receivedLatePenalty = _get(grade, 'receivedLatePenalty', null);
  const showGradeToStudent = _get(grade, 'showGradeToStudent');

  let studentGrade;
  const shouldShowGrade = shouldShowGrades(
    isStudentUser,
    percentComplete,
    showGradeToStudent,
    pastDue,
  );

  if (_isFinite(originalGrade) && shouldShowGrade) {
    // If grade exists, return that
    studentGrade = originalGrade;
  } else if (pastDue && showGradeToStudent !== false) {
    // If it's past due, return 0
    studentGrade = 0;
  } else {
    // Else grade is null
    studentGrade = null;
  }
  // If the grade has a number score, then it's considered "completed"
  const completed =
    grading_type === GRADING_TYPE.UNGRADED && percentComplete
      ? _isFinite(originalGrade) && percentComplete >= 1
      : _isFinite(originalGrade);
  const readyToReport = completed && shouldShowGrade;

  // Used to determine if the student has launched the assignment
  const userAsgnLaunchLogs = launchLogs.filter(
    (log) => log.assignmentId === id,
  );
  const userLaunchCount =
    (Array.isArray(userAsgnLaunchLogs) && userAsgnLaunchLogs.length) || 0;

  const isInstructorEntered = _get(grade, 'is_grade_edited', false);
  const isOverdue = _get(grade, 'isOverdue', false);

  // Used to display a status in the UI and drop grades
  const status = getStudentAssignmentStatus(
    readyToReport,
    pastDue,
    !!userLaunchCount,
    { percentComplete, isOverdue },
    isAssigned,
    isInstructorEntered,
  );

  // Check for due date setting AFTER setting status so we can support
  // assignments having a completed status when this setting is enabled
  if (
    shouldHideGradeUntilDueDate({
      gradesToReport,
      pastDue,
      isInstructorEntered,
    })
  ) {
    studentGrade = null;
  }

  let assignedPoints = assignmentAssignedPoints;
  if (studentAssignmentExceptions.length > 0 && !assignedPoints) {
    assignedPoints = _get(
      studentAssignmentExceptions,
      '[0].exception_value.assigned_points',
    );
  }

  const studentPoints = studentGrade * assignedPoints;
  const hasLaunched = userLaunchCount > 0;
  const gbCatId = gradebookCategoryId || 0;
  const gbCat = categories.find((cat) => cat.id === gbCatId);
  const gbCatName = (gbCat && gbCat.name) || 'Assignments';
  const gradeUpdatedAt = _get(grade, 'updated_at', null);
  const gradeCompletedAt = _get(grade, 'completed_at', null);
  const parent_id = _get(assignment, 'parent_id', null);

  const hasNoGrade = !Number(percentComplete) && originalGrade === null;
  const dateCompleted =
    hasNoGrade || !shouldShowGrade ? null : gradeCompletedAt || gradeUpdatedAt;

  return {
    ...assignment,
    id,
    name,
    tool,
    status,
    course_id,
    hasLaunched,
    activity_id,
    studentId,
    studentName,
    studentGrade,
    studentPoints,
    isAssigned,
    receivedLatePenalty,
    isBrokenSyncReefGrade,
    studentAssignmentDueAt: dueDate,
    assigned_points: assignedPoints,
    gradebook_category_id: gradebookCategoryId,
    gradebook_category_name: gbCatName,
    assignment_due_at,
    dateCompleted,
    isOverdue,
    isInstructorEntered,
    isFinalAssignment: isWritingFinalPaper(tool, parent_id),
    grading_type,
    parent_id,
    percentComplete,
    subtype,
    sequence,
  };
};
